import { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { Segment } from '../types/segment.type';
import { ADMIN_SEGMENTS_API_PATHS } from '../constants/segments-api-paths.constants';

type UseAdminGetSegmentByIdResult = {
  isLoading: boolean;
  isRefetching: boolean;
  segment: Segment | null;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetSegmentByIdResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  segment: null,
};

export function useAdminGetSegmentById({ segmentId }: { segmentId: string }): UseAdminGetSegmentByIdResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<Segment | undefined>(ADMIN_SEGMENTS_API_PATHS.GET_SEGMENT_BY_ID(segmentId));

      setResult({ isLoading: false, isRefetching: false, segment: response ?? null });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération du segment'),
        { variant: 'error' }
      );
      setResult((previous) => ({ ...previous, isLoading: false, isRefetching: false }));
    }
  }, [segmentId, enqueueSnackbar]);

  useEffect(() => {
    setResult(INITIAL_STATE);
  }, [segmentId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
